import React from "react";
import moment from "moment";
import lodashGet from "lodash/get";
import { useSelector, useDispatch } from "react-redux";
//material ui
import withStyles from "@material-ui/styles/withStyles";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import Divider from "@material-ui/core/Divider";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import MuiToolbar from "@material-ui/core/Toolbar";
import Chip from "@material-ui/core/Chip";
//charts
import ReactEchartsCore from "echarts-for-react/lib/core";
import echarts from "echarts/lib/echarts";
import "echarts/lib/chart/bar";
import "echarts/lib/chart/gauge";
import "echarts/lib/component/legend";
import "echarts/lib/component/tooltip";
//icons
import ExcelIcon from "mdi-material-ui/FileExcel";
import ArrowBack from "@material-ui/icons/ArrowBack";
//jazasoft
import { PageFooter, Button, RestMethods, FETCH_START, FETCH_END } from "jazasoft";
//local
import Report from "./Report";
import CardHeader from "../../../components/CardHeader";
//helpers
import { dataProvider } from "../../../App";
import handleError from "../../../utils/handleError";
import { getWeightedMean, isEmpty, toFixed } from "../../../utils/helpers";
import { colorSecondary, colorTomatoRed, colorLightGreen } from "../../../utils/colors";
import { MacroReportId } from "../../../constants";

const styles = theme => ({
  root: {
    margin: "1.5em 0"
  },
  card: {
    margin: "1em 0"
  },
  cardContent: {
    padding: 0,
    overflowX: "auto"
  },
  displayCenter: {
    display: "flex",
    justifyContent: "center"
  },
  spacer: {
    flexGrow: 1
  },
  actions: {
    color: theme.palette.text.secondary
  },
  noMarginPadding: {
    margin: 0,
    padding: 0
  }
});

const getEchartOptions =
  name =>
  ({ barChart, markerEfficiency }) => {
    if (name === "barChart") {
      if (!barChart || Object.keys(barChart).length === 0) return {};
      return {
        color: [colorSecondary.main],
        tooltip: {
          trigger: "axis",
          axisPointer: {
            type: "shadow"
          }
        },
        xAxis: [
          {
            type: "category",
            data: Object.keys(barChart),
            axisLabel: {
              rotate: 45
            }
          }
        ],
        yAxis: [
          {
            type: "value",
            show: false,
            axisLabel: {
              formatter: "{value}%"
            }
          }
        ],
        series: [
          {
            name: "efficiency",
            type: "bar",
            barWidth: 20,
            data: Object.keys(barChart).map(month => barChart[month]),
            itemStyle: {
              normal: {
                color: colorSecondary.main,
                label: {
                  show: true,
                  position: "top",
                  formatter: "{c}"
                }
              }
            }
          }
        ]
      };
    } else if (name === "efficiency") {
      if (!markerEfficiency) return {};
      return {
        tooltip: {
          formatter: "{a} <br/>{b} : {c}%"
        },
        series: [
          {
            name: "Efficiency",
            type: "gauge",
            min: 50,
            max: 100,
            splitNumber: 5, // Split the number of segments, the default is 5
            axisLine: {
              lineStyle: {
                color: [
                  [0.5, colorTomatoRed.main],
                  [0.8, colorSecondary.main],
                  [1, colorLightGreen.main]
                ],
                width: 4
              }
            },
            axisTick: {
              splitNumber: 10, // How many segments per split
              length: 10, // Attribute length control line length
              lineStyle: {
                color: "auto"
              }
            },
            axisLabel: {
              textStyle: {
                color: "auto",
                fontSize: 10
              }
            },
            splitLine: {
              show: true, // The default display, the property show control is displayed or not
              length: 15, // Attribute length control line length
              lineStyle: {
                color: "auto"
              }
            },
            pointer: {
              width: 5
            },
            detail: {
              formatter: "{value}%",
              textStyle: {
                color: "auto",
                fontWeight: "bold",
                fontSize: 16
              },
              offsetCenter: [0, "60%"]
            },
            data: [{ value: toFixed(markerEfficiency, 1) }]
          }
        ]
      };
    }
  };

const MarkerEfficiencyReportView = props => {
  const { classes, filter, history } = props || {};

  const dispatch = useDispatch();
  const i18nMessages = useSelector(state => state.i18n?.messages || {});

  const onDownload = () => {
    const orderList = props.orderList;
    const fileName = "MARKER_EFFICIENCY.xlsx";
    const orders = orderList.filter(o => {
      let result = true;
      if (filter.start && filter.end) {
        result = o.orderCloseDate >= moment(filter.start).startOf("day").valueOf() && o.orderCloseDate <= moment(filter.end).endOf("day").valueOf();
      }
      if (result && filter.customerList && filter.customerList.length > 0) {
        result = filter.customerList.map(({ name }) => name).includes(o.customer);
      }
      if (result && filter.season) {
        result = filter.season === o.season;
      }
      if (result && filter.line) {
        result = filter.line === o.line;
      }
      if (result && filter.productList && filter.productList.length > 0) {
        result = filter.productList.map(({ name }) => name).includes(o.product);
      }
      if (result && filter.supplierList && filter.supplierList.length > 0) {
        result = filter.supplierList.map(({ name }) => name).includes(o.supplier);
      }
      if (result && filter.fabricPatternList && filter.fabricPatternList.length > 0) {
        result = filter.fabricPatternList.map(({ name }) => name).includes(o.fabricPattern);
      }
      if (result && filter.fabricCategoryList && filter.fabricCategoryList.length > 0) {
        result = filter.fabricCategoryList.map(({ name }) => name).includes(o.fabricCategory);
      }
      return result;
    });

    const columns = [
      { dataKey: "orderCloseDate", title: "Close Date", type: "date" },
      // { dataKey: "cuttingCompletedDate", title: "Cut Date", type: "date" },
      // { dataKey: "line", title: "Line/Floor/Factory" },
      { dataKey: "poRef", title: "ERP No" },
      { dataKey: "customer", title: "Customer" },
      { dataKey: "season", title: "Season" },
      { dataKey: "style", title: "Style" },
      { dataKey: "styleColor", title: "Style Color" },
      { dataKey: "styleNo", title: "Style No" },
      { dataKey: "part", title: "Part" },
      { dataKey: "lot", title: "Lot" },
      { dataKey: "orderQty", title: "Order Qty" },
      { dataKey: "cutQty", title: "Cut Qty" },
      { dataKey: "rejectedQty", title: "Rejected Qty" },
      { dataKey: "itemCode", title: "FAB Item Code" },
      { dataKey: "supplier", title: "Supplier" },
      { dataKey: "fabricPattern", title: "FAB Pattern" },
      { dataKey: "fabDirection", title: "Fab Direction" },
      { dataKey: "csv", title: "CSV" },
      { dataKey: "fabRepeat", title: "Fab Repeat (IN CM)" },
      { dataKey: "width", title: "FAB BOM Width (IN CM)" },
      { dataKey: "actualWidth", title: "FAB Acutal Width (IN CM)" },
      { dataKey: "markerEfficiency", title: "Marker Efficiency" },
      { dataKey: "markerUpdatedBy", title: "Marker Updated By" }
      // { dataKey: "remarks", title: "Remarks" },
    ].map(c => ({ ...c, title: lodashGet(i18nMessages, `resources.report.arr.excelColumns.${c.dataKey}`) || c.title }));

    const rowData = orders
      .map(r => {
        return {
          ...r,
          fabRepeat:
            !isEmpty(r.fabricPattern) && r.fabricPattern?.toLowerCase() !== "solid"
              ? isEmpty(r.repeatLength) && isEmpty(r.repeatWidth)
                ? ""
                : isEmpty(r.repeatLength)
                ? `0 X ${r.repeatWidth}`
                : isEmpty(r.repeatWidth)
                ? `${r.repeatLength} X 0`
                : `${r.repeatLength} X ${r.repeatWidth}`
              : null
        };
      })
      .map((row, idx) => ({ rowNo: idx + 1, cellList: columns.map(c => ({ dataKey: c.dataKey, value: row[c.dataKey] })) }));

    const options = {
      url: `export/excel`,
      method: "post",
      responseType: "arraybuffer",
      data: [
        {
          sheetNo: 0,
          sheetName: "MARKER_EFFICIENCY",
          autoSizeColumn: true,
          excelData: { columnList: columns.map((c, idx) => ({ ...c, colNo: idx + 1 })), rowList: rowData }
        }
      ],
      params: { filename: fileName }
    };
    dispatch({ type: FETCH_START });
    dataProvider(RestMethods.CUSTOM, undefined, options)
      .then(response => {
        if (response.status === 200) {
          const header = response.headers["content-disposition"];
          var filename = header ? header.match(/filename="(.+)"/)[1] : "MARKER_EFFICIENCY.xlsx";

          let blob = new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          });
          let link = document.createElement("a");
          link.href = window.URL.createObjectURL(blob);
          link.download = filename;
          link.click();
        }
        dispatch({ type: FETCH_END });
      })
      .catch(err => {
        handleError(err, dispatch);
        dispatch({ type: FETCH_END });
      });
  };

  const _getCategoryData = (list, months) => {
    let result = {};
    if (list.length > 0) {
      const markerEfficiency = getWeightedMean(list.map(e => ({ key: e.markerEfficiency, weight: e.allocated - e.returned })));
      const orderList = list.map(e => ({ ...e, month: moment(e.orderCloseDate).format("MMM, YY") }));
      let barChart = {};
      months.forEach(month => {
        const data = orderList.filter(e => e.month === month);
        const efficiency = data.length > 0 ? getWeightedMean(data.map(e => ({ key: e.markerEfficiency, weight: e.allocated - e.returned }))) : 0.0;
        barChart[month] = toFixed(efficiency, 1);
      });
      const fabric = list.reduce((acc, el) => acc + (el.allocated - el.returned), 0);
      result = { fabric, markerEfficiency, barChart };
    }
    return result;
  };

  const renderCard = ({ title, poCount, data, index }) => {
    return (
      <Grid key={index} item xs={12} sm={6}>
        <Card>
          <MuiToolbar>
            <div className={classes.title}>
              <CardHeader title={title} className={classes.noMarginPadding} />
            </div>
            <div className={classes.spacer} />
            <div className={classes.actions}>
              <Chip label={poCount} className={classes.noMarginPadding} />
            </div>
          </MuiToolbar>
          <Divider />
          <CardContent className={classes.cardContent}>
            <Grid container alignItems="center" justify="space-between">
              <Grid item xs={8}>
                <ReactEchartsCore
                  echarts={echarts}
                  option={getEchartOptions("barChart")({ barChart: data.barChart })}
                  style={{ height: "235px", width: "100%" }}
                  opts={{ renderer: "svg" }}
                  className="react_for_echarts"
                />
              </Grid>
              <Grid item xs={4}>
                <ReactEchartsCore
                  echarts={echarts}
                  option={getEchartOptions("efficiency")({ markerEfficiency: data.markerEfficiency })}
                  style={{ height: "190px", width: "100%" }}
                  opts={{ renderer: "svg" }}
                  className="react_for_echarts"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    );
  };

  const orderList = props.orderList.map(e => ({
    ...e,
    month: moment(e.orderCloseDate).format("MMM, YY")
  }));

  if (orderList.length === 0) {
    return (
      <div style={{ marginTop: "3em" }}>
        <Typography className={classes.displayCenter} color="primary" variant="subtitle1">
          Data not available
        </Typography>
      </div>
    );
  }

  let months = [];
  let curr = moment(filter.end);
  while (curr.valueOf() > moment(filter.start).valueOf()) {
    months.unshift(curr.format("MMM, YY"));
    curr = curr.subtract(1, "months");
  }

  let markerEfficiencyReport = []; // category -> month -> {meterChart: [weightedEfficiecny], barChart: {[dataKey]: value} }  . dataKey = month, value = weightedEfficiency

  ["Solid", "Stripes", "Checks"].forEach(fabricPattern => {
    if (fabricPattern === "Checks") {
      const breakPoints = [0, 4, 16];
      for (let i = 0; i < breakPoints.length; i++) {
        const lowerLimit = breakPoints[i];
        const upperLimit = i + 1 < breakPoints.length ? breakPoints[i + 1] : Infinity;
        const list = orderList.filter(
          e => e.repeatLength && e.repeatWidth && e.repeatLength * e.repeatWidth >= lowerLimit && e.repeatLength * e.repeatWidth < upperLimit
        );
        let titleSuffix = i === 0 ? "Up to (2x2) cm" : i === 1 ? "(2x2) to (4x4) cm" : "Above (4x4) cm";
        const title = "Repeat: " + titleSuffix;
        markerEfficiencyReport[i + 5] = {
          title,
          poCount: list.length,
          index: i + 1,
          data: _getCategoryData(list, months)
        };
      }
    } else if (fabricPattern === "Stripes") {
      const breakPoints = [0, 1, 2];
      for (let i = 0; i < breakPoints.length; i++) {
        const lowerLimit = breakPoints[i];
        const upperLimit = i + 1 < breakPoints.length ? breakPoints[i + 1] : Infinity;
        const list = orderList.filter(
          e =>
            ((e.repeatLength && !e.repeatWidth) || (!e.repeatLength && e.repeatWidth)) &&
            (e.repeatLength ? e.repeatLength >= lowerLimit && e.repeatLength < upperLimit : e.repeatWidth >= lowerLimit && e.repeatWidth < upperLimit)
        );
        let titleSuffix = i === 0 ? "Up to 1 cm" : i === 1 ? "1 to 2 cm" : "Above 2 cm";
        const title = "Repeat: " + titleSuffix;
        markerEfficiencyReport[i + 2] = {
          title,
          poCount: list.length,
          index: i + 5,
          data: _getCategoryData(list, months)
        };
      }
    } else {
      let list = orderList.filter(e => !(e.repeatLength || e.repeatWidth) && (isEmpty(e.csv) || e.csv === "No"));
      let title = "No Repeat: Normal";
      markerEfficiencyReport[0] = {
        title,
        poCount: list.length,
        index: 0,
        data: _getCategoryData(list, months)
      };

      list = orderList.filter(e => !(e.repeatLength || e.repeatWidth) && !isEmpty(e.csv) && e.csv !== "No");
      title = "No Repeat: CSV";
      markerEfficiencyReport[1] = {
        title,
        poCount: list.length,
        index: 4,
        data: _getCategoryData(list, months)
      };
    }
  });

  return (
    <div className={classes.root}>
      <Grid container spacing={2}>
        {markerEfficiencyReport.filter(e => e.data.fabric > 0).map(record => renderCard(record))}
      </Grid>

      <PageFooter className={classes.displayCenter}>
        <Button label="Back" variant="contained" onClick={() => history.goBack()}>
          <ArrowBack />
        </Button>
        <Button label="Download" variant="contained" style={{ color: "green", marginLeft: "1em" }} onClick={onDownload}>
          <ExcelIcon />
        </Button>
      </PageFooter>
    </div>
  );
};

const StyledMarkerEfficiencyReport = withStyles(styles)(MarkerEfficiencyReportView);

const MarkerEfficiencyReport = props => (
  <Report title="Marker Efficiency Report" defaultMonths={6} macroReportId={MacroReportId.MARKER_EFFICIENCY} {...props}>
    <StyledMarkerEfficiencyReport {...props} />
  </Report>
);

export default MarkerEfficiencyReport;
